import {jsx} from 'panel';

import {QueryBlockEl} from 'common/query-builder/query-block';
import {serializeAttributes} from 'common/util';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {instrumentWithTimer} from 'common/util/workflow-timing';
import {ResourceType} from 'common/report/constants';
import {getIconForEvent} from 'common/report/util/events';
import {PATH_SEPARATOR} from 'common/constants';

import type {QueryEntrySection} from '../index';
import {QuerySuperEntry} from '../types';

export function RowSelector({
  el,
  entry,
  index,
  isPlaceholder,
  selectorIcon,
  handleSelect,
  handleCreate,
  selected,
}: {
  el: QueryEntrySection;
  entry: QuerySuperEntry;
  index: number;
  isPlaceholder: boolean;
  selectorIcon?: string;
  handleSelect?: (ev: CustomEvent) => void;
  handleCreate?: () => void;
  selected?: any;
}) {
  const hasNewUI = el.hasNewQBUI();
  const hasNewCountMenu = el.hasNewCountMenu();
  const {index: activeIndex, part: activePart} = el.state.openEntry;

  const iconMapping = {
    [ResourceType.Cohort]: `community`,
  };

  selectorIcon ||= iconMapping[entry.type] ?? getIconForEvent(entry?.event);

  const hasProperty = `property` in entry && entry.property?.value;

  return (
    <>
      <QueryBlockEl
        key={`entry-selector-${index}${isPlaceholder ? `-placeholder` : ``}`}
        class={{
          'is-disabled': entry.readOnlyEvent,
          'is-placeholder': isPlaceholder,
          'has-property': !hasNewCountMenu && hasProperty,
        }}
        attrs={serializeAttributes({
          ...el.helpers.getQueryBlockConfig(entry, index),
          autofocus: index === 0 && el.attr(`autofocus`) && isPlaceholder && !el.isStagedEntry(0),
          'button-size': el.attr(`button-theme`) === ButtonTheme.Browse ? ButtonSize.Large : ButtonSize.Medium,
          elref: `entrySelector`,
          open: isPlaceholder && activeIndex === index && activePart === el.helpers.EntryPart.Value,
          'data-group-id': el.attr(`data-group-id`),
          'enable-enhanced-list-filters': el.attr(`enable-enhanced-list-filters`),
          'enable-workspace-filter-restrictions': el.attr(`enable-workspace-filter-restrictions`),
          'exclude-custom-events': el.attr(`exclude-custom-events`),
          'hide-edit-controls': el.attr(`hide-edit-controls`),
          'inline-cohorts-override': el.attr(`inline-cohorts-override`),
          'project-has-cohorts': el.attr(`project-has-cohorts`),
          'project-id': el.attr(`project-id`),
          selected: selected ?? (entry.event || null),
          theme:
            el.attr(`button-theme`) ||
            (hasNewUI ? ButtonTheme.QueryBuilder : entry.readOnlyEvent ? ButtonTheme.Tertiary : ButtonTheme.Select),
          'track-name': el.attr(`track-name`),
          'selector-icon': el.attr(`enable-event-icon`) || !hasNewUI ? selectorIcon : ``,
          'selector-type': `button`,
          'user-id': el.attr(`user-id`),
          disabled: el.attr(`disabled`),
          'workspace-id': el.attr(`workspace-id`),
          'prefix-label': entry.prefixLabel,
          'suffix-label': !hasNewCountMenu && hasNewUI && hasProperty && PATH_SEPARATOR,
        })}
        props={{contextPanelParentElement: el.helpers.getContextPanelParentEl(index)}}
        on={{
          close: () => el.helpers.setActiveEntry(`close`, {index, part: el.helpers.EntryPart.Value}),
          savedCustomEvent: el.helpers.propagateEvent,
          open: instrumentWithTimer(() => el.helpers.setActiveEntry(`open`, {index, part: el.helpers.EntryPart.Value})),
          remove: () => el.helpers.onEventActionClick(el.helpers.Action.Remove, index),
          select: handleSelect ?? ((ev: CustomEvent) => el.helpers.onEventSelected(ev, index)),
          track: (ev: CustomEvent) => el.helpers.trackEntrySelectorEvent(ev, index),
          create: handleCreate,
        }}
      />
    </>
  );
}
